import {
  forwardRef,
  type ButtonHTMLAttributes,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { cn } from './cn';
import { focusRing } from './focus-ring';

/**
 * Button variants (design-spec §3 "Buttons"). Every variant is a pill.
 *
 * - accent  — bg accent, white. "Search", "Choose a service →", "Confirm booking ✓".
 * - dark    — bg ink, white. "Sign in", "Book now", "Continue — pick a time".
 * - outline — surface bg, 1px line-strong, ink. "View salon", "Back", "Share".
 * - light   — on-dark fill for dark panels. "List your business →".
 * - ghost   — link-coloured text with a soft hover pill. "Cancel booking", "Clear all".
 * - link    — inline text link (no box). "Read more", "Change location".
 */
export type ButtonVariant =
  | 'accent'
  | 'dark'
  | 'outline'
  | 'light'
  | 'ghost'
  | 'link';

/**
 * Sizes map onto the spec's control heights (§2.8):
 * - sm 40 — nav "Sign in", service-row "Book"
 * - md 44 — card "Book now"
 * - lg 50 — aside CTA "Choose a service", mobile sticky CTA
 * - xl 54 — "Confirm booking", auth CTA
 */
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

const base =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-sans font-semibold ' +
  'transition-colors duration-150 select-none ' +
  focusRing + ' ' +
  'disabled:cursor-not-allowed disabled:opacity-55 aria-disabled:cursor-not-allowed aria-disabled:opacity-55';

const variants: Record<ButtonVariant, string> = {
  accent: 'bg-sl-accent text-white hover:brightness-95 active:brightness-90',
  dark: 'bg-sl-ink text-white hover:bg-sl-ink/90',
  outline:
    'bg-sl-surface text-sl-ink border border-sl-line-strong hover:bg-sl-bg hover:border-sl-label/60',
  light: 'bg-sl-on-dark text-sl-ink hover:bg-white',
  ghost:
    'bg-transparent text-sl-link hover:text-sl-link-hover hover:bg-sl-accent-tint',
  link: 'bg-transparent text-sl-link hover:text-sl-link-hover underline-offset-4 hover:underline rounded-sm',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-ctl-sm px-5 text-[14px] hit-44',
  md: 'h-touch px-[22px] text-[14.5px]',
  lg: 'h-ctl-cta px-6 text-[15px] font-bold',
  xl: 'h-ctl-lg px-7 text-[15.5px] font-bold',
};

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Stretch to the container width (aside and auth CTAs). */
  fullWidth?: boolean;
  /** Shows a spinner, disables the control and sets aria-busy. */
  loading?: boolean;
  /** Icon before the label (back actions: ←). */
  leadingIcon?: ReactNode;
  /** Icon after the label (forward actions: →, confirm: ✓). */
  trailingIcon?: ReactNode;
  className?: string;
  children?: ReactNode;
}

type NativeButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  keyof CommonProps
>;

export type ButtonProps = CommonProps &
  NativeButtonProps & {
    /** Render the single child element (e.g. a router `<Link>`) with button styles. */
    asChild?: boolean;
    /** Render a plain `<a href>` (external links). */
    href?: string;
    /** Render a react-router `<Link to>` (internal links). */
    to?: string;
    target?: string;
    rel?: string;
  };

export function buttonClasses({
  variant = 'dark',
  size = 'md',
  fullWidth,
  className,
}: Pick<
  CommonProps,
  'variant' | 'size' | 'fullWidth' | 'className'
> = {}): string {
  return cn(
    base,
    variants[variant],
    variant === 'link'
      ? 'h-auto p-0 font-semibold text-[length:inherit]'
      : sizes[size],
    fullWidth && 'w-full',
    className
  );
}

export const Button = forwardRef<HTMLElement, ButtonProps>(function Button(
  {
    variant = 'dark',
    size = 'md',
    fullWidth,
    loading = false,
    leadingIcon,
    trailingIcon,
    className,
    children,
    asChild,
    href,
    to,
    target,
    rel,
    type,
    disabled,
    onClick,
    ...rest
  },
  ref
) {
  const classes = buttonClasses({ variant, size, fullWidth, className });
  const isDisabled = disabled || loading;
  const lead = loading ? (
    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
  ) : (
    leadingIcon
  );

  if (asChild) {
    return (
      <Slot
        ref={ref}
        className={classes}
        aria-busy={loading || undefined}
        aria-disabled={isDisabled || undefined}
        {...rest}
        onClick={isDisabled ? undefined : onClick}
        // The child may be a link or carry its own onClick: stop both while
        // disabled/loading (capture runs before the child's bubble handlers).
        onClickCapture={(e: MouseEvent<HTMLElement>) => {
          if (isDisabled) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        {lead}
        <Slottable>{children}</Slottable>
        {trailingIcon}
      </Slot>
    );
  }

  if (to !== undefined || href !== undefined) {
    // Links can't be `disabled`; mark them aria-disabled and swallow clicks.
    const linkProps = {
      className: classes,
      target,
      rel: rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined),
      'aria-busy': loading || undefined,
      'aria-disabled': isDisabled || undefined,
      onClick: (e: MouseEvent<HTMLAnchorElement>) => {
        if (isDisabled) {
          e.preventDefault();
          return;
        }
        (
          onClick as ((ev: MouseEvent<HTMLAnchorElement>) => void) | undefined
        )?.(e);
      },
      ...(rest as Record<string, unknown>),
    };
    if (to !== undefined) {
      return (
        <Link ref={ref as Ref<HTMLAnchorElement>} to={to} {...linkProps}>
          {lead}
          {children}
          {trailingIcon}
        </Link>
      );
    }
    return (
      <a ref={ref as Ref<HTMLAnchorElement>} href={href} {...linkProps}>
        {lead}
        {children}
        {trailingIcon}
      </a>
    );
  }

  return (
    <button
      ref={ref as Ref<HTMLButtonElement>}
      type={type ?? 'button'}
      className={classes}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      onClick={onClick}
      {...rest}
    >
      {lead}
      {children}
      {trailingIcon}
    </button>
  );
});
